import { type FC } from 'react';
import type { Payment, PaymentPayload, PaymentStatusObject } from '../../types';
import { getPaymentStatusBadgeColor, STATUS_LABELS } from '../../constants/orderStatuses';
import type { PaymentHistoryEntry } from '../../services/paymentsHistoryService';

interface PaymentsTabProps {
    payments:       Payment[];
    paymentHistory: PaymentHistoryEntry[];
}

// ── Helpers ────────────────────────────────────────────────────────────────────

// Parses the payment payload, which may arrive as a JSON string or a plain object.
// Returns null if the value is absent or unparseable.
function parsePayload(raw: Payment['payload']): PaymentPayload | null {
    if (!raw) return null;
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw) as PaymentPayload;
        } catch {
            return null;
        }
    }
    return raw as PaymentPayload;
}

// Returns true if this payment was settled via wallet credit rather than a card gateway.
function isWalletPayment(payment: Payment, payload: PaymentPayload | null): boolean {
    return (
        payment.payment_type     === 'WALLET' ||
        payment.payment_provider === 'wallet' ||
        payload?.covered_total   === true
    );
}

// Derives the display amount in rands (÷ 100) from the payment row and its payload.
// captured_amount_cents is the authoritative "money actually taken" figure —
// it's null until a capture attempt happens, so a partially-captured payment
// (e.g. because a picking shortfall reduced what's owed) correctly shows less
// than the originally authorized/requested amount. For payments that haven't
// been captured yet (pending, requires_payment_method, failed), it falls back
// to the authorized amount, then the originally requested amount, so those
// rows still show a meaningful figure rather than R0.00.
function resolveDisplayAmount(
    payment: Payment,
    payload: PaymentPayload | null,
    isWallet: boolean,
): number {
    if (isWallet) {
        const rawCents = payload?.order_total_cents ?? payment.amount ?? 0;
        return Number(rawCents) / 100;
    }

    const rawCents = payment.captured_amount_cents != null
        ? payment.captured_amount_cents
        : (payment.authorized_amount_cents ?? (payment.amount || payload?.amount || payload?.amount_due_cents || 0));

    return Number(rawCents) / 100;
}

// Resolves the canonical status string for a payment row.
function resolveStatus(
    payment: Payment,
    payload: PaymentPayload | null,
    isWallet: boolean,
): string {
    const statusName =
        typeof payment.status === 'object'
            ? (payment.status as PaymentStatusObject)?.name
            : payment.status;

    if (statusName) return statusName;

    if (isWallet && payload?.covered_total) return 'PAYMENT_SUCCEEDED';
    if (payload?.resultDetails?.ExtendedDescription === 'Transaction Successful') return 'SUCCESS';
    return 'PENDING';
}

// Builds the human-readable details string shown in the Details column.
// latestReason (from payments_history) takes priority once present — it's
// written at the moment of each status transition, so it stays accurate
// even after the raw gateway payload snapshot goes stale (e.g. a refund
// applied outside the original webhook flow, which never touches `payload`).
function resolveDetailsText(
    payment: Payment,
    payload: PaymentPayload | null,
    isWallet: boolean,
    latestReason: string | null,
): string {
    if (isWallet) {
        const walletRands = (Number(payload?.wallet_applied_cents ?? 0) / 100).toFixed(2);
        return `Wallet credit applied: R${walletRands}`;
    }

    if (latestReason) return latestReason;

    return (
        payload?.resultDetails?.ExtendedDescription ||
        payload?.result?.description ||
        (payload?.result?.status ? `Status: ${payload.result.status}` : null) ||
        (payload?.id ? `ID: ${payload.id}` : null) ||
        `ID: ${payment.checkout_id || 'N/A'}`
    );
}

// Maps a raw status name to its human-readable label, falling back to a
// title-cased version of the raw string.
function formatStatusLabel(status: string | null): string {
    if (!status) return '—';
    return STATUS_LABELS[status] ?? status.toLowerCase().replace(/_/g, ' ');
}

// Returns the colour classes for a history timeline dot based on the destination status.
function historyDotColor(status: string | null): string {
    if (!status) return 'bg-stone-300';
    const s = status.toUpperCase();
    if (s.includes('REFUND')) return 'bg-[#8dc63f]';
    if (s.includes('SUCCEEDED') || s === 'SUCCESS' || s === 'COMPLETED') return 'bg-emerald-500';
    if (s.includes('FAILED') || s.includes('DECLINED') || s.includes('CANCELLED') || s.includes('VOIDED') || s === 'CAPTURE_FAILED') return 'bg-rose-500';
    if (s.includes('PRE_AUTHORIZED') || s.includes('PROCESSING')) return 'bg-sky-500';
    return 'bg-amber-400';
}

// True when less was captured than was originally authorized/requested —
// e.g. a picking shortfall meant the gateway only captured for what shipped.
// Used to show the original amount alongside the reduced captured amount.
function isPartialCapture(payment: Payment): boolean {
    const authorizedCents = payment.authorized_amount_cents ?? payment.amount;
    return (
        payment.captured_amount_cents != null &&
        authorizedCents != null &&
        payment.captured_amount_cents < authorizedCents
    );
}

// True when the payment's status indicates money was returned to the
// customer. There's no dedicated refund table or refunded-amount column in
// this schema — the only signal is the status itself, and captured_amount_cents
// is left unchanged when a refund happens. So we can only say "this payment's
// captured amount was refunded" as a whole; partial refunds aren't
// distinguishable from full ones with the data currently tracked.
function isRefunded(status: string): boolean {
    return status.toUpperCase().includes('REFUND');
}

// Builds a map of payments_id -> most recent transition reason, from the
// payment_history entries (already ordered oldest-first by the service, so
// the last entry seen per payments_id is the current one).
function buildLatestReasonByPaymentId(paymentHistory: PaymentHistoryEntry[]): Record<string, string | null> {
    const map: Record<string, string | null> = {};
    for (const entry of paymentHistory) {
        if (entry.reason) map[entry.payments_id] = entry.reason;
    }
    return map;
}

// Builds a Rand-formatted amount note for a status transition, sourced from
// the actual payment row (authorized_amount_cents/captured_amount_cents) —
// not parsed from the free-text `reason`, which is written for humans and
// isn't a reliable source to parse programmatically. Returns null when the
// transition type doesn't have a meaningful amount to show, or the payment
// row can't be found (e.g. it was hard-deleted).
function buildHistoryAmountNote(entry: PaymentHistoryEntry, paymentsById: Record<string, Payment>): string | null {
    const payment = paymentsById[entry.payments_id];
    if (!payment) return null;

    const fmtR = (cents: number | null | undefined) =>
        cents != null ? `R${(Number(cents) / 100).toFixed(2)}` : null;

    const authorized = fmtR(payment.authorized_amount_cents ?? payment.amount);
    const captured   = fmtR(payment.captured_amount_cents);
    const status     = (entry.current_status ?? '').toUpperCase();

    if (status.includes('REFUND')) {
        return captured ? `${captured} refunded` : null;
    }
    if (status.includes('SUCCEEDED') || status.includes('CAPTURED')) {
        if (captured && authorized && payment.captured_amount_cents !== (payment.authorized_amount_cents ?? payment.amount)) {
            return `${captured} of ${authorized} authorised`;
        }
        return captured;
    }
    if (status.includes('PRE_AUTH') || status.includes('PRE-AUTH')) {
        return authorized ? `${authorized} authorised` : null;
    }
    return null;
}

// ── Component ──────────────────────────────────────────────────────────────────

const PaymentsTab: FC<PaymentsTabProps> = ({ payments, paymentHistory }) => {
    if (!payments || payments.length === 0) {
        return (
            <div className="rounded-lg border border-dashed border-stone-200 p-12 text-center">
                <p className="text-sm text-stone-500 italic">No payment records found for this order.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">

            {/* ── Transaction table ── */}
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wide">Transaction History</h3>
                <div className="rounded-lg border border-stone-200 bg-white overflow-hidden shadow-sm">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-stone-50 border-b border-stone-200">
                        <tr>
                            <th className="px-6 py-3 text-xs font-semibold text-stone-700 uppercase tracking-wide">Date</th>
                            <th className="px-6 py-3 text-xs font-semibold text-stone-700 uppercase tracking-wide">Method</th>
                            <th className="px-6 py-3 text-xs font-semibold text-stone-700 uppercase tracking-wide">Status</th>
                            <th className="px-6 py-3 text-xs font-semibold text-stone-700 uppercase tracking-wide">Details</th>
                            <th className="px-6 py-3 text-right text-xs font-semibold text-stone-700 uppercase tracking-wide">Amount</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-200">
                        {(() => {
                            const latestReasonByPaymentId = buildLatestReasonByPaymentId(paymentHistory);
                            return payments.map((payment) => {
                                const payload       = parsePayload(payment.payload);
                                const isWallet      = isWalletPayment(payment, payload);
                                const displayAmount = resolveDisplayAmount(payment, payload, isWallet);
                                const rawStatus     = resolveStatus(payment, payload, isWallet);
                                const detailsText   = resolveDetailsText(payment, payload, isWallet, latestReasonByPaymentId[payment.id] ?? null);
                                const formattedStatus = rawStatus.toLowerCase().replace(/_/g, ' ');
                                const partialCapture  = !isWallet && isPartialCapture(payment);
                                const refunded         = !isWallet && isRefunded(rawStatus);

                                return (
                                    <tr key={payment.id} className="hover:bg-stone-50 transition-colors">
                                        {/* Date */}
                                        <td className="px-6 py-4 text-stone-600 whitespace-nowrap">
                                            {new Date(payment.created_at).toLocaleString('en-ZA', {
                                                day: 'numeric', month: 'short', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit',
                                            })}
                                        </td>

                                        {/* Payment method */}
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                {isWallet ? (
                                                    <span className="font-medium text-stone-900">Wallet</span>
                                                ) : (
                                                    <>
                                                    <span className="font-medium text-stone-900 capitalize">
                                                        {payload?.card?.network || payload?.paymentBrand || payment.payment_provider || 'Card'}
                                                    </span>
                                                        {(payload?.card?.last4 || payload?.card?.last4Digits) && (
                                                            <span className="text-[10px] text-stone-500 font-mono">
                                                            **** {payload.card.last4 || payload.card.last4Digits}
                                                        </span>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>

                                        {/* Status badge */}
                                        <td className="px-6 py-4">
                                        <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-bold ring-1 ring-inset shadow-sm capitalize ${getPaymentStatusBadgeColor(rawStatus)}`}>
                                            {formattedStatus}
                                        </span>
                                        </td>

                                        {/* Details */}
                                        <td className="px-6 py-4 text-xs text-stone-500 max-w-xs">
                                            <p className="truncate" title={detailsText}>
                                                {detailsText}
                                            </p>
                                        </td>

                                        {/* Amount */}
                                        <td className="px-6 py-4 text-right whitespace-nowrap">
                                            {refunded ? (
                                                <div>
                                                    <p className="text-stone-400 line-through text-xs font-normal">
                                                        R {(Number(payment.authorized_amount_cents ?? payment.amount) / 100).toFixed(2)}
                                                    </p>
                                                    <p className="font-semibold text-[#5a7f28]">R {displayAmount.toFixed(2)} refunded</p>
                                                </div>
                                            ) : partialCapture ? (
                                                <div>
                                                    <p className="text-stone-400 line-through text-xs font-normal">
                                                        R {(Number(payment.authorized_amount_cents ?? payment.amount) / 100).toFixed(2)}
                                                    </p>
                                                    <p className="font-semibold text-amber-600">R {displayAmount.toFixed(2)}</p>
                                                </div>
                                            ) : (
                                                <span className="font-semibold text-stone-900">R {displayAmount.toFixed(2)}</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            });
                        })()}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Payment status timeline ── */}
            {paymentHistory.length > 0 && (
                <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wide">Status Transitions</h3>
                    <div className="rounded-lg border border-stone-200 bg-white shadow-sm p-6">
                        <ol className="relative border-l border-stone-200 space-y-6 ml-3">
                            {(() => {
                                const paymentsById: Record<string, Payment> = {};
                                payments.forEach(p => { paymentsById[p.id] = p; });
                                return paymentHistory.map((entry) => {
                                    const amountNote = buildHistoryAmountNote(entry, paymentsById);
                                    return (
                                        <li key={entry.id} className="ml-6">
                                            {/* Timeline dot */}
                                            <span className={`absolute -left-[7px] flex h-3.5 w-3.5 items-center justify-center rounded-full ring-2 ring-white ${historyDotColor(entry.current_status)}`} />

                                            <div className="flex flex-wrap items-center gap-2">
                                                {/* Previous → Current */}
                                                {entry.previous_status ? (
                                                    <>
                                                <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${getPaymentStatusBadgeColor(entry.previous_status)}`}>
                                                    {formatStatusLabel(entry.previous_status)}
                                                </span>
                                                        <svg className="h-3 w-3 text-stone-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                        </svg>
                                                    </>
                                                ) : (
                                                    <span className="text-xs text-stone-400 italic">Initial state</span>
                                                )}
                                                <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${getPaymentStatusBadgeColor(entry.current_status ?? '')}`}>
                                            {formatStatusLabel(entry.current_status)}
                                        </span>
                                                {amountNote && (
                                                    <span className="text-xs font-semibold text-stone-500">{amountNote}</span>
                                                )}
                                            </div>

                                            {/* Timestamp + optional reason */}
                                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                                <time className="text-xs text-stone-400">
                                                    {new Date(entry.created_at).toLocaleString('en-ZA', {
                                                        day: 'numeric', month: 'short', year: 'numeric',
                                                        hour: '2-digit', minute: '2-digit',
                                                    })}
                                                </time>
                                                {entry.reason && (
                                                    <span className="text-xs text-stone-500 italic">"{entry.reason}"</span>
                                                )}
                                            </div>
                                        </li>
                                    );
                                });
                            })()}
                        </ol>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentsTab;